import React, { useState } from 'react';

/**
 * Small, consistent building blocks for the control panel (desktop
 * sidebar and phone More sheet): a collapsible group, a label/control row,
 * a switch, a stepper, a range with its value, a select and a button.
 * Styled by practice-panel.css; the phone sheet enlarges them for touch.
 */

const OPEN_KEY = 'musiker-desktop-groups';
const readOpenState = () => {
  try { return JSON.parse(localStorage.getItem(OPEN_KEY) || '{}'); } catch (e) { return {}; }
};

export const Group = ({ id, title, defaultOpen = true, badge, children }) => {
  const [open, setOpen] = useState(() => {
    const saved = readOpenState();
    return id in saved ? Boolean(saved[id]) : defaultOpen;
  });
  const toggle = () => {
    const next = !open;
    setOpen(next);
    try {
      const saved = readOpenState();
      saved[id] = next;
      localStorage.setItem(OPEN_KEY, JSON.stringify(saved));
    } catch (e) { /* ignore */ }
  };
  return (
    <section className={`dps-group ${open ? 'open' : ''}`}>
      <button type="button" className="dps-group-head" onClick={toggle} aria-expanded={open}>
        <span className="dps-group-title">{title}</span>
        {badge && <span className="dps-group-badge">{badge}</span>}
        <span className="dps-group-chev" aria-hidden="true">&#8250;</span>
      </button>
      {open && <div className="dps-group-body">{children}</div>}
    </section>
  );
};

// `hint` shows under the label; `tip` explains the row on hover instead,
// marked with an ⓘ, where the space is tight
export const Row = ({ label, hint, tip, stacked, children }) => (
  <div className={`dps-row ${stacked ? 'stacked' : ''}`} title={tip}>
    <div className="dps-row-label">
      <span>{label}{tip && <span className="dps-row-tip" aria-hidden="true"> ⓘ</span>}</span>
      {hint && <small>{hint}</small>}
    </div>
    <div className="dps-row-control">{children}</div>
  </div>
);

export const Switch = ({ checked, onChange, disabled, label }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    className={`dps-switch ${checked ? 'on' : ''}`}
    disabled={disabled}
    onClick={() => onChange(!checked)}
  >
    <span className="dps-switch-knob" />
  </button>
);

export const Stepper = ({ display, onDec, onInc, canDec = true, canInc = true, disabled, onReset, decLabel = '−', incLabel = '+', decTitle, incTitle }) => (
  <div className="dps-stepper">
    <button type="button" onClick={onDec} disabled={disabled || !canDec} aria-label={decTitle} title={decTitle}>{decLabel}</button>
    <span
      className={`dps-stepper-value ${onReset ? 'tappable' : ''}`}
      onClick={disabled ? undefined : onReset}
      title={onReset ? 'Click to reset' : undefined}
    >
      {display}
    </span>
    <button type="button" onClick={onInc} disabled={disabled || !canInc} aria-label={incTitle} title={incTitle}>{incLabel}</button>
  </div>
);

export const Range = ({ value, min, max, step = 1, onChange, disabled, format, label }) => (
  <div className="dps-range">
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      disabled={disabled}
      aria-label={label}
      onChange={(e) => onChange(Number(e.target.value))}
    />
    <span className="dps-range-value">{format ? format(value) : value}</span>
  </div>
);

export const Select = ({ value, onChange, disabled, label, children }) => (
  <select className="dps-select" value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled} aria-label={label}>
    {children}
  </select>
);

export const Button = ({ onClick, disabled, primary, title, children }) => (
  <button type="button" className={`dps-btn ${primary ? 'primary' : ''}`} onClick={onClick} disabled={disabled} title={title}>
    {children}
  </button>
);

// Small square icon toggle used in the parts table (hear / show)
export const IconToggle = ({ checked, onChange, disabled, label, children }) => (
  <button
    type="button"
    className={`dps-icon-toggle ${checked ? 'on' : ''}`}
    aria-pressed={checked}
    aria-label={label}
    title={label}
    disabled={disabled}
    onClick={() => onChange(!checked)}
  >
    {children}
  </button>
);
